import { findPath, getMap, TILE } from '@wahala/maps';
import { dist2, lineOfSight, slide } from './geometry.js';
import { nextInt, nextU32, seedRng } from './rng.js';
import { canSee } from './views.js';
export const BOY_LINES = ['settle', 'wetin', 'pass', 'here'];
/** The agbero levers of a match that has them. */
export const boysConfig = (s) => s.config.areaBoys;
const postsOf = (map) => map.areaBoyPosts ?? [];
const own = (rec, key) => Object.hasOwn(rec, key) ? rec[key] : undefined;
/** The commotion round a latched keke lasts this long after each bang. */
const BANG_MS = 600;
/** A closed demand comes back after this long while he is still on the keke. */
const PROMPT_AGAIN_MS = 6_000;
/** How many agberos a round of n players puts out (Razaq besides): 1 for every perPlayers, rounded up, with a post free to move to. */
export function boysFor(c, n, posts) {
    const want = Math.ceil(n / c.perPlayers);
    return Math.max(0, Math.min(want, posts > 1 ? posts - 1 : posts));
}
/**
 * Agberos for a match whose config has them and whose map has posts, or undefined. Placed and the
 * tickets dealt from their own generator, so no other draw in the match moves.
 */
export function createAreaBoys(s) {
    const c = s.config.areaBoys;
    const map = getMap(s.mapId);
    const posts = postsOf(map);
    if (!c || posts.length === 0)
        return undefined;
    const rng = seedRng((s.seed ^ 0x41524541) >>> 0);
    const razaq = map.razaqPost;
    // a seeded shuffle of the other posts; the first `count` are manned
    const order = posts.map((_, i) => i).filter((i) => i !== razaq);
    for (let i = order.length - 1; i > 0; i--) {
        const j = nextInt(rng, i + 1);
        [order[i], order[j]] = [order[j], order[i]];
    }
    const count = Math.min(order.length, boysFor(c, s.order.length, posts.length));
    const boy = (id, post, movesAt) => {
        const at = posts[post];
        return {
            id,
            post,
            pos: { x: at.x, y: at.y },
            path: [],
            movesAt,
            follow: null,
            followUntil: 0,
            on: null,
            onUntil: 0,
            pinned: false,
        };
    };
    const boys = [];
    // staggered, so the agberos do not all move at once
    for (let i = 0; i < count; i++)
        boys.push(boy(`b${i + 1}`, order[i], s.now + Math.floor((c.moveMs * (i + 1)) / count)));
    if (razaq !== undefined && posts[razaq])
        boys.push({ ...boy('razaq', razaq, Number.MAX_SAFE_INTEGER), razaq: true });
    const st = {
        rng,
        boys,
        banked: {},
        taken: {},
        ticket: {},
        alone: {},
        prompt: {},
        route: {},
        sends: {},
        bangs: [],
    };
    // the tickets: a seeded share of the seats, drivers and VIOs alike
    const seats = [...s.order];
    for (let i = seats.length - 1; i > 0; i--) {
        const j = nextInt(rng, i + 1);
        [seats[i], seats[j]] = [seats[j], seats[i]];
    }
    const tickets = new Set(seats.slice(0, Math.round(seats.length * c.ticketShare)));
    for (const id of s.order) {
        st.banked[id] = 0;
        st.taken[id] = 0;
        st.ticket[id] = tickets.has(id);
        st.alone[id] = {};
        st.prompt[id] = null;
        st.route[id] = [];
        st.sends[id] = s.players[id]?.role === 'impostor' ? c.sends : 0;
    }
    return st;
}
/** The coins in this keke's purse now: fares earned, less what it banked and what the levy took. */
export function purseOf(s, id) {
    const b = s.areaBoys;
    const earned = s.rides?.earned[id] ?? 0;
    if (!b)
        return earned;
    return Math.max(0, earned - (b.banked[id] ?? 0) - (b.taken[id] ?? 0));
}
/** The agbero on this keke now, or null. */
export const latchedBy = (s, id) => s.areaBoys?.boys.find((b) => b.on === id) ?? null;
/** An agbero on this keke: it crawls (the rule set's speed takes the latch share). */
export const latched = (s, id) => latchedBy(s, id) !== null;
/** What the levy would take from this keke now: the levy, or all of a thinner purse. */
export const levyOf = (s, id) => Math.min(boysConfig(s).levy, purseOf(s, id));
const onRoad = (p) => p.alive && !p.departed && p.vent === null;
function bank(s, id, where, events) {
    const b = s.areaBoys;
    b.route[id] = [];
    const coins = purseOf(s, id);
    if (coins <= 0)
        return;
    b.banked[id] = (b.banked[id] ?? 0) + coins;
    events.push({ t: 'purse.banked', playerId: id, coins, where });
}
/** Walk toward the front of the path, dropping each point reached; true once the path is done. */
function walk(map, b, speed) {
    let left = speed;
    while (left > 0 && b.path.length > 0) {
        const to = b.path[0];
        const dx = to.x - b.pos.x;
        const dy = to.y - b.pos.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d <= left) {
            b.pos = slide(map, b.pos, dx, dy);
            b.path.shift();
            left -= Math.max(1, Math.floor(d));
            continue;
        }
        const k = left / d;
        b.pos = slide(map, b.pos, Math.round(dx * k), Math.round(dy * k));
        left = 0;
    }
    return b.path.length === 0;
}
/** He lets go of the keke: back to his post on foot. */
function letGo(s, b, paid, events) {
    const st = s.areaBoys;
    const id = b.on;
    if (id === null)
        return;
    b.on = null;
    b.onUntil = 0;
    b.pinned = false;
    b.path = [];
    st.prompt[id] = null;
    // unpaid: this agbero leaves the keke alone a while
    if (!paid)
        st.alone[id][b.id] = s.now + boysConfig(s).leaveAloneMs;
    events.push({ t: 'boys.gone', playerId: id, boy: b.id, paid });
}
function latchOn(s, b, p, pinned, events) {
    const c = boysConfig(s);
    b.on = p.id;
    b.pinned = pinned;
    b.onUntil = s.now + (pinned ? c.pinMs : c.latchMs);
    b.follow = null;
    b.followUntil = 0;
    b.path = [];
    events.push({ t: 'boys.latched', playerId: p.id, boy: b.id, pinned });
    // a sent one pins: no levy will move him
    if (!pinned) {
        s.areaBoys.prompt[p.id] = { boy: b.id, at: s.now };
        events.push({ t: 'boys.prompt', playerId: p.id, boy: b.id, line: 'settle' });
    }
}
/** A keke without a ticket passing close enough, in the open, that he is not leaving alone. */
function prey(s, st, map, b) {
    const r = boysConfig(s).radius * TILE;
    let best = null;
    let bestD = r * r;
    for (const id of s.order) {
        const p = s.players[id];
        if (!onRoad(p) || st.ticket[id] || latched(s, id))
            continue;
        if ((st.alone[id]?.[b.id] ?? 0) > s.now)
            continue;
        const d = dist2(b.pos, p.pos);
        if (d > bestD || !lineOfSight(map, b.pos, p.pos))
            continue;
        best = p;
        bestD = d;
    }
    return best;
}
/**
 * Each tick on the road, after the kekes move. `free` says who is out on the road at all (an
 * arrested or locked-up driver is not: the arrest layer passes it in).
 */
export function tickAreaBoys(s, events, free = () => true) {
    const st = s.areaBoys;
    if (!st)
        return;
    const c = boysConfig(s);
    const map = getMap(s.mapId);
    const posts = postsOf(map);
    st.bangs = st.bangs.filter((x) => x.until > s.now);
    for (const b of st.boys) {
        // on a keke: he goes where it goes, banging on it, until it pays or he tires of it
        if (b.on !== null) {
            const t = own(s.players, b.on);
            if (!t || !onRoad(t) || !free(t) || s.now >= b.onUntil) {
                letGo(s, b, false, events);
            }
            else {
                b.pos = { ...t.pos };
                if (s.tick % 6 === 0)
                    st.bangs.push({ pos: { ...t.pos }, until: s.now + BANG_MS });
                // a closed demand comes back while he is still on it
                if (!b.pinned && st.prompt[t.id] === null && (s.now - b.onUntil) % PROMPT_AGAIN_MS === 0) {
                    st.prompt[t.id] = { boy: b.id, at: s.now };
                    events.push({ t: 'boys.prompt', playerId: t.id, boy: b.id, line: 'wetin' });
                }
                continue;
            }
        }
        // sent after a driver: he hunts them down and pins them
        if (b.follow !== null) {
            const t = own(s.players, b.follow);
            if (!t || !onRoad(t) || !free(t) || s.now >= b.followUntil) {
                b.follow = null;
                b.followUntil = 0;
                b.path = [];
            }
            else {
                const r = c.radius * TILE;
                if (dist2(b.pos, t.pos) <= r * r && lineOfSight(map, b.pos, t.pos)) {
                    latchOn(s, b, t, true, events);
                    continue;
                }
                if (s.tick % 10 === 0 || b.path.length === 0)
                    b.path = findPath(map, b.pos, t.pos);
                walk(map, b, c.walkSpeed);
                continue;
            }
        }
        // working his post: a keke without a ticket that passes close gets him on it
        const p = prey(s, st, map, b);
        if (p && free(p)) {
            latchOn(s, b, p, false, events);
            continue;
        }
        if (!b.razaq && s.now >= b.movesAt) {
            b.movesAt += c.moveMs;
            const manned = new Set(st.boys.map((x) => x.post));
            const open = posts.map((_, i) => i).filter((i) => !manned.has(i) && i !== map.razaqPost);
            if (open.length)
                b.post = open[nextInt(st.rng, open.length)];
            // the generator moves on every move, open post or not, so later draws never depend on it
            nextU32(st.rng);
            b.path = [];
        }
        // back to his post, on foot
        const home = posts[b.post];
        if (dist2(b.pos, home) > 30 * 30) {
            if (b.path.length === 0)
                b.path = findPath(map, b.pos, home);
            walk(map, b, c.walkSpeed);
        }
    }
    // stopped at the park office: the purse banks
    const office = map.stations.find((x) => x.kind === 'button');
    if (office) {
        const r = c.bankRadius * TILE;
        for (const id of s.order) {
            const p = s.players[id];
            const slow = p.vel.x * p.vel.x + p.vel.y * p.vel.y <= 36;
            if (!onRoad(p) || !slow || dist2(p.pos, office.pos) > r * r)
                continue;
            if (purseOf(s, id) > 0)
                bank(s, id, 'park', events);
        }
    }
}
/** After a ride stop: a drop at a stop not yet on this route counts; enough different stops is a full route, and it banks. */
export function rideStopped(s, p, events) {
    const st = s.areaBoys;
    if (!st)
        return;
    const paid = events.find((e) => e.t === 'ride.paid' && e.playerId === p.id);
    if (paid?.t !== 'ride.paid')
        return;
    const route = st.route[p.id] ?? [];
    if (!route.includes(paid.stationId))
        route.push(paid.stationId);
    st.route[p.id] = route;
    if (route.length >= boysConfig(s).routeStops)
        bank(s, p.id, 'route', events);
}
/** A meeting clears the road: every agbero lets go and stops hunting, no demand, no commotion. */
export function stopAreaBoys(s) {
    const st = s.areaBoys;
    if (!st)
        return;
    for (const b of st.boys) {
        b.on = null;
        b.onUntil = 0;
        b.pinned = false;
        b.follow = null;
        b.followUntil = 0;
        b.path = [];
    }
    for (const id of s.order)
        st.prompt[id] = null;
    st.bangs = [];
}
/** Why this keke may not answer the levy now, or null. */
export function checkAnswer(s, p) {
    const st = s.areaBoys;
    if (!st)
        return 'NOT_IN_MODE';
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    if (!p.alive)
        return 'NOT_ALIVE';
    if (st.prompt[p.id])
        return null;
    // an agbero hanging off your keke takes his levy whenever you hand it over (one sent to pin you takes none)
    const on = latchedBy(s, p.id);
    return on && !on.pinned ? null : 'NO_PROMPT';
}
/**
 * Pay (the levy from the purse, or all of a thinner one: he gets off and the keke has a ticket for
 * the rest of the round) or not now (the demand closes; he stays on and asks again). Paying needs
 * no open demand while he hangs on: the levy goes to the agbero on the keke.
 */
export function answerBoy(s, p, choice, events) {
    const st = s.areaBoys;
    const boy = st.prompt[p.id]?.boy ?? latchedBy(s, p.id)?.id;
    st.prompt[p.id] = null;
    const b = st.boys.find((x) => x.id === boy);
    if (choice === 'refuse' || !b)
        return;
    const coins = levyOf(s, p.id);
    st.taken[p.id] = (st.taken[p.id] ?? 0) + coins;
    st.ticket[p.id] = true;
    events.push({ t: 'boys.settled', playerId: p.id, boy: b.id, coins, line: 'pass' });
    letGo(s, b, true, events);
}
/** The free agbero nearest to a keke (not on one, not hunting, not Razaq), or null. */
function nearestFree(st, at) {
    let best = null;
    let bestD = Number.POSITIVE_INFINITY;
    for (const b of st.boys) {
        if (b.follow !== null || b.on !== null || b.razaq)
            continue;
        const d = dist2(b.pos, at);
        if (d < bestD) {
            best = b;
            bestD = d;
        }
    }
    return best;
}
/** Why this VIO may not send the agberos after this keke now, or null. */
export function checkSend(s, p, targetId) {
    const st = s.areaBoys;
    if (!st)
        return 'NOT_IN_MODE';
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    if (p.role !== 'impostor')
        return 'NOT_IMPOSTOR';
    if (!p.alive)
        return 'NOT_ALIVE';
    if (p.vent !== null)
        return 'IN_VENT';
    if ((st.sends[p.id] ?? 0) <= 0)
        return 'NO_CHARGES';
    const t = own(s.players, targetId);
    if (!t || t.id === p.id || !onRoad(t) || t.role !== 'crew' || latched(s, t.id))
        return 'BAD_TARGET';
    if (!canSee(s, p, t.pos))
        return 'OUT_OF_RANGE';
    return nearestFree(st, t.pos) ? null : 'NO_BOY';
}
export function sendBoys(s, p, targetId, events) {
    const st = s.areaBoys;
    const t = s.players[targetId];
    const b = nearestFree(st, t.pos);
    st.sends[p.id] = (st.sends[p.id] ?? 0) - 1;
    b.follow = t.id;
    b.followUntil = s.now + boysConfig(s).sendMs;
    b.path = [];
    events.push({ t: 'boys.sent', playerId: p.id, boy: b.id, targetId: t.id });
}
/** The end prize each player on the winning side gets: about an average purse (mean fares), kept in its bounds. */
export function endPrize(s) {
    const c = s.config.areaBoys;
    if (!c || !s.areaBoys || c.endPrize === 'none')
        return 0;
    const n = s.order.length;
    const total = s.order.reduce((t, id) => t + (s.rides?.earned[id] ?? 0), 0);
    const mean = n === 0 ? 0 : Math.floor(total / n);
    return Math.max(c.endPrizeMin, Math.min(c.endPrizeMax, mean));
}
/**
 * What the round pays this player into the coin wallet (the api counts it in, inside the daily
 * cap): banked coins always; the purse and the end prize only on the winning side, and only for a
 * player who stayed to the end. A player who left keeps only what they banked.
 */
export function payoutOf(s, id) {
    const st = s.areaBoys;
    const p = s.players[id];
    const purse = purseOf(s, id);
    const banked = st?.banked[id] ?? 0;
    const won = s.winner !== null && p.team === s.winner.team && !p.departed;
    const prize = won ? endPrize(s) : 0;
    return { purse, banked, taken: st?.taken[id] ?? 0, endPrize: prize, coins: banked + (won ? purse + prize : 0) };
}
/** Commands the agbero layer handles (the rule set routes them here). */
export const BOY_COMMANDS = new Set(['boys.answer', 'boys.send']);
//# sourceMappingURL=areaboys.js.map